<template>
  <card header="Measuring points">
    <template v-slot:actions>
      <card-action
        icon="add"
        tooltip="Create a new measurring point configuration"
        @click="onCreate"
      />
    </template>
    <div>
      <v-data-table v-if="models.length" :headers="headers" :items="models" item-key="id"
        class="measuring-points-table"
        :rows-per-page-items="[5]"
      >
        <template v-slot:items="props">
          <tr :key="props.item.id">
            <td>{{ props.item.resourceId | translate }}</td>
            <td :title="props.item.endpointPath">{{ props.item.endpointPath }}</td>
            <td>{{ props.item.unit }}</td>
            <td>{{ props.item.readIntervalInSeconds }}</td>
            <td>{{ props.item.allowedForBilling }}</td>
            <td>{{ tags(props.item).join(', ') }}</td>
            <td>
              <div class="color-gradient" :style="iconStyle(props.item.colorGradient)" />
            </td>
            <td>
              <div class="measuring-points-table-actions">
                <v-tooltip bottom class="measuring-points-table-action">
                  <v-btn slot="activator" @click="onEdit(props.item)" icon flat>
                    <v-icon small>edit</v-icon>
                  </v-btn>
                  <span>{{ 'common.button.edit' | translate }}</span>
                </v-tooltip>
                <v-tooltip bottom class="measuring-points-table-action">
                  <v-btn slot="activator" @click="onDelete(props.item.id)" icon flat>
                    <v-icon small>delete</v-icon>
                  </v-btn>
                  <span>{{ 'common.button.delete' | translate }}</span>
                </v-tooltip>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
      <div v-else>
        None defined 
      </div>
      <edit-measuring-point-dialog 
        :show="showDialog"
        :model="modelToEdit" 
        @closed="showDialog = false"
      />
    </div>
  </card>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import { namespace } from 'vuex-class'

import Card from '../../ui/Card.vue'
import CardAction from '../../ui/CardAction.vue'

import EditMeasuringPointDialog from './EditMeasuringPointDialog.vue'

import { FunctionDefinitionViewModel } from '../../../store/modules/functionDefinitionEditorUi/models'

import { MeasuringPoint } from '../../../../../eco-domain-store-modules/src/systemConfiguration/models'

import { DropdownOption, Gradients } from '../../../../../shared-components/src'

const FunctionDefinitionEditorUi = namespace('functionDefinitionEditorUi')

@Component({
  components: {
    Card,
    CardAction,
    EditMeasuringPointDialog,
  },
})
export default class MeasuringPointsDetail extends Vue {
  @FunctionDefinitionEditorUi.Getter public functionDefinition: FunctionDefinitionViewModel
  @FunctionDefinitionEditorUi.Getter public functionDefinitionMeasuringPointTagOptions: DropdownOption[]
  @FunctionDefinitionEditorUi.Mutation public removeFunctionDefinitionMeasuringPoint: (payload: string) => void

  public showDialog = false
  public modelToEdit: MeasuringPoint | null = null

  public get models() {
    return this.functionDefinition.measuringPoints
  }

  public get headers() {
    return [
      {
        text: 'Name',
        value: 'resoureId',
        align: 'left',
        width: '15%',
      },
      {
        text: 'Endpoint path',
        value: 'endpointPath',
        align: 'left',
        width: '15%',
      },
      {
        text: 'Unit',
        value: 'unit',
        align: 'left',
        width: '10%',
      },
      {
        text: 'Read interval (s)',
        value: 'readIntervalInSeconds',
        align: 'left',
        width: '10%',
      },
      {
        text: 'Allowed for billing',
        value: 'allowedForBilling',
        align: 'left',
        width: '15%',
      },
      {
        text: 'Tags',
        value: 'tags',
        align: 'left',
        width: '20%',
      },
      {
        text: 'Color',
        value: 'colorGradient',
        sortable: false,
        width: '5%',
      },
      {
        text: '',
        sortable: false,
        width: '10%',
      },    
    ]
  }

  public tags(measuringPoint: MeasuringPoint) {
    return measuringPoint.tags
      .map(tag => this.functionDefinitionMeasuringPointTagOptions.find(_ => _.id === tag)?.label ?? tag)
  }

  public iconStyle(colorGradient: string) {
    return Gradients.style(colorGradient, true)
  }

  public onCreate() {
    this.modelToEdit = null
    this.showDialog = true
  }

  public onEdit(model: MeasuringPoint) {
    this.modelToEdit = model
    this.showDialog = true
  }

  public onDelete(id: string) {
    this.removeFunctionDefinitionMeasuringPoint(id)
  }
}
</script>
<style lang="scss" scoped>
  .cardbody {
    margin-left: -10px;
  }
  .popUpButton {
    flex: 0;
    padding-left: 10px;
  }
  .field {
    height: 70px;
  }
  .field .label {
    padding-left: 10px;
    overflow: hidden;
    max-height: 40px;
  }
  thead tr {
    border-bottom: 1px solid white!important;
  }
  .measuring-points-table {
    .measuring-points-table-actions {
      display: flex;
      justify-content: flex-end;
      .measuring-points-table-action {
        width: 36px;
      }
    }
  }
  .color-gradient {
    height: 25px;
    width: 25px;
    border-radius: 5px;
  }
</style>
<style lang="css">
.measuring-points-table .theme--dark.v-table {
  background-color: transparent !important;
  table-layout: fixed;
  width: 100%;
}
.measuring-points-table .v-datatable__actions {
  background-color: transparent !important;
}
</style>